import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Link } from '@mui/material';
import { GridColumns, GridRenderCellParams } from '@mui/x-data-grid-pro';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FileManagerTabBase, { FileRow, FileSystemType } from './FileManagerTabBase';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Viewer } from '../../util/Constants';
import { fetchFileSearchResults } from '../../features/FileManager/FileManagerActions';
import { selectFileSearchResultFiles, selectIsSearchLoading, fetchFileSearchFilesSuccess } from '../../features/FileManager/FileManagerSlice';
import { DataLakeFileModel } from '../../gql-types.generated';

interface FileSearchProps {
    viewer: Viewer | undefined;
    onDirectoryClick: (path: string) => void;
    onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const FileSearch: React.FC<FileSearchProps> = (props) => {
    const { viewer, onDirectoryClick, onTabChange } = props;
    const dispatch = useAppDispatch();
    
    const [searchText, setSearchText] = useState('');
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [noRecordsMessage, setNoRecordsMessage] = useState<string|undefined>(undefined);
    const [noResultsMessage, setNoResultsMessage] = useState<string|undefined>(undefined);

    const searchResults = useAppSelector(selectFileSearchResultFiles);
    const isSearchLoading = useAppSelector(selectIsSearchLoading);

     const refresh = useCallback(() => {
        if (searchText && searchPerformed) {
            dispatch(fetchFileSearchResults(searchText));
        }
    }, [searchText, searchPerformed]);
    
    useEffect(() => {
        if (!searchPerformed) {
            setNoRecordsMessage("Enter a search term to find files.");
            setNoResultsMessage(undefined);
        } else if (!searchResults || searchResults.length === 0) {
            setNoRecordsMessage(undefined);
            setNoResultsMessage("No files found matching your search.");
        }
    }, [searchPerformed, searchResults]);

    const handleSearchChange = (newSearchText: string) => {
        setSearchText(newSearchText);
        // Clear search when empty
        if (newSearchText.length === 0) {
            setSearchPerformed(false);
            dispatch(fetchFileSearchFilesSuccess(undefined));
        }
    };

    const handleSearchSubmit = (searchText: string) => {
        if (searchText.length >= 3) {
            setSearchPerformed(true);
            dispatch(fetchFileSearchResults(searchText));
        }
    };

    const handleDirectoryClick = useCallback((path: string) => {
        // Switch to Active Directory tab
        onTabChange({} as React.SyntheticEvent, 0);
        // Load the directory
        onDirectoryClick(path);
    }, [onTabChange, onDirectoryClick]);

    const columns = useMemo<GridColumns>(() => [
        {
            field: 'name',
            headerName: 'FILE NAME',
            flex: 2,
            sortable: true,
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as FileRow;
                return (
                    <Box display="flex" alignItems="center" width="100%">
                        <Box mr={1}>
                            <InsertDriveFileIcon color='action' />
                        </Box>
                        <Box sx={{ overflow: 'hidden' }}>
                            <div className='ediDataGridCellOverflowHidden'>{row.name}</div>
                        </Box>
                    </Box>
                );
            }
        },
        {
            field: 'path',
            headerName: 'DIRECTORY',
            flex: 2,
            sortable: true,
            renderCell: (params: GridRenderCellParams) => {
                // Extract directory path (everything before the last /)
                const directoryPath = params.row.path.substring(0, params.row.path.lastIndexOf('/'));
                return (
                    <Link 
                        onClick={() => handleDirectoryClick(directoryPath)}
                        className='ediDataGridCellOverflowHidden'
                        sx={{ cursor: 'pointer' }}
                    >
                        {directoryPath}
                    </Link>
                );
            }
        }
    ], [handleDirectoryClick]);

    const rows = useMemo(() => (searchResults || []).map((file: DataLakeFileModel) => ({
        id: file.path as string,
        name: file.name as string,
        type: FileSystemType.File,
        isDirectory: false,
        path: file.path as string,
        lastModifiedTime: file.lastModifiedTime as string,
        createdTime: file.createdTime as string,
        fileSizeDisplay: file.fileSize as string,
        fileSize: file.fileSizeBytes as number,
    } as FileRow)), [searchResults]);

    return (
        <FileManagerTabBase
            viewer={viewer}
            onRefreshTab={refresh}
            columns={columns}
            rows={rows}
            loading={isSearchLoading}
            checkboxSelection
            showFileActionButtons
            defaultSortField="name"
            defaultSortDirection="asc"
            overlayErrorTitle="Unable to load Files"
            noResultsMessage={noResultsMessage}
            noRowsMessage={noRecordsMessage}
            searchPlaceholder="Enter at least 3 characters to Search..."
            onSearchChange={handleSearchChange}
            onSearchSubmit={handleSearchSubmit}
            searchValue={searchText}
            minSearchLength={3}
            gridProps={{
                getRowClassName: () => { return 'file-row'; },
            }}
        />
    );
};

export default FileSearch;
